import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  message,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { api, type SensorWriteReq, type Zone } from "../api";
import InterfaceModal, {
  type InterfaceFormValues,
} from "./InterfaceModal";

// fmtBpsLabel turns a stored bits-per-second value into a Wansight-style
// short label (e.g. 10000000000 → "10 Gbps", 900000000 → "900 Mbps"). We
// use it in the Monitored Interfaces table cells.
function fmtBpsLabel(bps: number | undefined): string {
  if (!bps || bps <= 0) return "—";
  if (bps >= 1_000_000_000 && bps % 1_000_000_000 === 0) {
    return `${bps / 1_000_000_000} Gbps`;
  }
  if (bps >= 1_000_000_000) return `${(bps / 1_000_000_000).toFixed(1)} Gbps`;
  if (bps >= 1_000_000) return `${Math.round(bps / 1_000_000)} Mbps`;
  return `${Math.round(bps / 1_000)} kbps`;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  // When editing, pass sensorId. The form loads the current sensor +
  // interfaces from the API.
  sensorId?: number;
}

type IfaceRow = SensorWriteReq["interfaces"][number] & { key: number };

// Form values mirror SensorWriteReq but keep the multi-zone list as a
// single `zone_ids` field; we split primary out at submit time.
type FormValues = Omit<SensorWriteReq, "zone_id" | "zone_ids" | "interfaces"> & {
  zone_ids: number[];
};

export default function SensorForm({ open, onClose, onSaved, sensorId }: Props) {
  const [form] = Form.useForm<FormValues>();
  const [zones, setZones] = useState<Zone[]>([]);
  const [ifaces, setIfaces] = useState<IfaceRow[]>([]);
  const [busy, setBusy] = useState(false);

  // Interface add/edit modal state. editingKey === null = closed,
  // === undefined = adding fresh, === <number> = editing that key.
  const [editingKey, setEditingKey] = useState<number | null | undefined>(null);
  // Watch protocol to conditionally show Sampling Rate (only meaningful for
  // NetFlow / IPFIX — sFlow embeds the rate in every packet header itself).
  const protocol = Form.useWatch("protocol", form);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setZones(await api.zones());
      } catch (e) {
        message.error(String(e));
      }
      if (sensorId) {
        try {
          const d = await api.sensor(sensorId);
          // Pre-populate multi-select: prefer zone_ids from server, fall
          // back to the legacy single zone_id when the junction is empty.
          const zoneIDs =
            d.sensor.zone_ids && d.sensor.zone_ids.length > 0
              ? d.sensor.zone_ids
              : [d.sensor.zone_id];
          form.setFieldsValue({
            name: d.sensor.name,
            enabled: d.sensor.enabled,
            listen_ip: d.sensor.listen_ip,
            listen_port: d.sensor.listen_port,
            protocol: d.sensor.protocol as SensorWriteReq["protocol"],
            exporter_ip: d.sensor.exporter_ip,
            zone_ids: zoneIDs,
            spool_dir: "/opt/sflow/spool",
            sampling_rate: d.sensor.sampling_rate ?? 0,
            snmp_community: d.sensor.snmp_community ?? "",
            snmp_version: d.sensor.snmp_version ?? "v2c",
            snmp_port: d.sensor.snmp_port ?? 161,
            ip_validation_mode: d.sensor.ip_validation_mode ?? "on",
            stats_engine: d.sensor.stats_engine ?? "extended",
            traffic_class: d.sensor.traffic_class ?? "auto",
            color: d.sensor.color ?? "",
            flows_timeout_active: d.sensor.flows_timeout_active ?? 60,
            flows_timeout_inactive: d.sensor.flows_timeout_inactive ?? 30,
            granularity_seconds: d.sensor.granularity_seconds ?? 5,
          });
          setIfaces(
            d.interfaces.map((x, i) => ({
              key: i,
              if_index: x.if_index,
              name: x.name,
              direction: (x.direction || "auto") as "auto" | "in" | "out",
              color: x.color,
              stats_mode: (x.stats_mode || "extended") as "basic" | "extended",
              speed_in: x.speed_in ?? 0,
              speed_out: x.speed_out ?? 0,
            }))
          );
        } catch (e) {
          message.error(String(e));
        }
      } else {
        form.resetFields();
        form.setFieldsValue({
          enabled: true,
          listen_ip: "0.0.0.0",
          listen_port: 6343,
          protocol: "auto",
          exporter_ip: "0.0.0.0",
          spool_dir: "/opt/sflow/spool",
          sampling_rate: 0,
          snmp_community: "",
          snmp_version: "v2c",
          snmp_port: 161,
          ip_validation_mode: "on",
          stats_engine: "extended",
          traffic_class: "auto",
          color: "",
          flows_timeout_active: 60,
          flows_timeout_inactive: 30,
          granularity_seconds: 5,
        });
        setIfaces([]);
      }
    })();
  }, [open, sensorId, form]);

  async function onOk() {
    try {
      const values = await form.validateFields();
      const ids = values.zone_ids ?? [];
      if (ids.length === 0) {
        message.error("Pick at least one IP Zone");
        return;
      }
      // First selection becomes the primary (sensor.ipzone_id). The rest
      // are tracked in the sensor_ipzone junction. Backend accepts both
      // fields and deduplicates.
      const { zone_ids: _omit, ...restValues } = values;
      const body: SensorWriteReq = {
        ...restValues,
        zone_id: ids[0],
        zone_ids: ids,
        interfaces: ifaces.map(({ key: _k, ...ifRest }) => ifRest),
      };
      setBusy(true);
      if (sensorId) {
        await api.updateSensor(sensorId, body);
      } else {
        await api.createSensor(body);
      }
      message.success(sensorId ? "Sensor updated" : "Sensor created");
      onSaved();
      onClose();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  function openAddIface() {
    setEditingKey(undefined);
  }

  function openEditIface(key: number) {
    setEditingKey(key);
  }

  function removeIface(key: number) {
    setIfaces((xs) => xs.filter((x) => x.key !== key));
  }

  function saveIface(v: InterfaceFormValues) {
    if (editingKey === undefined) {
      // Insert new — preserve sort by if_index, but keep insertion order
      // for ties so the user can see what they just added at the bottom of
      // its index group.
      setIfaces((xs) =>
        [
          ...xs,
          {
            key: Date.now(),
            if_index: v.if_index,
            name: v.name,
            direction: v.direction,
            color: v.color,
            stats_mode: v.stats_mode,
            speed_in: v.speed_in,
            speed_out: v.speed_out,
          },
        ].sort((a, b) => a.if_index - b.if_index)
      );
    } else if (typeof editingKey === "number") {
      const k = editingKey;
      setIfaces((xs) =>
        xs.map((x) =>
          x.key === k
            ? {
                ...x,
                if_index: v.if_index,
                name: v.name,
                direction: v.direction,
                color: v.color,
                stats_mode: v.stats_mode,
                speed_in: v.speed_in,
                speed_out: v.speed_out,
              }
            : x
        )
      );
    }
    setEditingKey(null);
  }

  // Find the interface currently being edited for InterfaceModal initial.
  const editingIface =
    typeof editingKey === "number"
      ? ifaces.find((x) => x.key === editingKey)
      : undefined;

  const ifaceColumns: ColumnsType<IfaceRow> = [
    { title: "Index", dataIndex: "if_index", width: 80 },
    { title: "Interface Name", dataIndex: "name", ellipsis: true },
    {
      title: "Direction",
      dataIndex: "direction",
      width: 100,
      render: (v: string) => v.charAt(0).toUpperCase() + v.slice(1),
    },
    // Per-interface "Stats" column intentionally omitted: sflow's
    // aggregator is per-sensor, so the Stats Engine knob lives on the
    // Flow Sensor Configuration modal (sensor scope) — having it here
    // too implied per-interface granularity that the runtime doesn't
    // actually honour. Re-add when the per-interface refactor lands
    // (plan §P1.2 follow-up).
    {
      title: "Speed IN",
      dataIndex: "speed_in",
      width: 100,
      render: (v?: number) => fmtBpsLabel(v),
    },
    {
      title: "Speed OUT",
      dataIndex: "speed_out",
      width: 100,
      render: (v?: number) => fmtBpsLabel(v),
    },
    {
      title: "",
      width: 90,
      render: (_: unknown, row) => (
        <Space size={4}>
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEditIface(row.key)}
          />
          <Button
            type="text"
            size="small"
            icon={<DeleteOutlined />}
            danger
            onClick={() => removeIface(row.key)}
          />
        </Space>
      ),
    },
  ];

  // 2-column form layout matching Wansight's "Flow Sensor Configuration"
  // modal: right-aligned narrow label column, field column to the right,
  // two pairs per row.
  const labelCol = { span: 9 };
  const wrapperCol = { span: 15 };

  return (
    <Modal
      open={open}
      title={
        sensorId ? `Flow Sensor Configuration — #${sensorId}` : "Add sFlow source"
      }
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={busy}
      width={780}
      okText="Save"
    >
      <Form<FormValues>
        form={form}
        labelCol={labelCol}
        wrapperCol={wrapperCol}
        labelAlign="right"
        colon={true}
        size="small"
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Sensor Name"
              rules={[{ required: true }]}
            >
              <Input placeholder="edge-sensor-01" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="enabled" label="Enabled" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              label="Chart Color"
              tooltip="Colour of this source's band on the Bits/s chart. Click Auto to use the default palette."
            >
              <Space>
                <Form.Item name="color" noStyle>
                  <Input type="color" style={{ width: 56, padding: 2 }} />
                </Form.Item>
                <Button size="small" onClick={() => form.setFieldsValue({ color: "" })}>
                  Auto
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="listen_ip"
              label="Listener IP"
              rules={[{ required: true }]}
            >
              <Input placeholder="0.0.0.0" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="listen_port"
              label="Listener Port"
              rules={[{ required: true }]}
            >
              <InputNumber min={1} max={65535} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="protocol"
              label="Flow Protocol"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: "auto", label: "Auto-detect" },
                  { value: "sflow", label: "sFlow v5" },
                  { value: "nfv5", label: "NetFlow v5" },
                  { value: "nfv9", label: "NetFlow v9" },
                  { value: "ipfix", label: "IPFIX" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="exporter_ip"
              label="Exporter IP"
              rules={[{ required: true }]}
            >
              <Input placeholder="0.0.0.0 (any)" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="zone_ids"
              label="IP Zones"
              tooltip="First zone selected is the primary zone (stored in sensor.ipzone_id). Flows match against the union of all selected zones."
              rules={[
                {
                  required: true,
                  validator: (_r, v: number[] | undefined) =>
                    v && v.length > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error("pick at least one zone")),
                },
              ]}
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="pick one or more zones"
                options={zones.map((z) => ({
                  value: z.id,
                  label: `${z.id} — ${z.name}`,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="spool_dir" label="Spool Dir">
              <Input placeholder="/opt/sflow/spool" />
            </Form.Item>
          </Col>
        </Row>

        {/* SNMP credentials — used by the Add Interface modal to walk
            IF-MIB on the exporter and populate the picker. Empty community
            disables discovery; the operator can still type rows manually. */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="snmp_community"
              label="SNMP Community"
              tooltip="SNMPv2c read-only community used by the Add Interface picker. Leave blank to disable interface discovery."
            >
              <Input placeholder="public (leave blank to disable picker)" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="snmp_version"
              label="Version"
              labelCol={{ span: 12 }}
              wrapperCol={{ span: 12 }}
            >
              <Select
                options={[
                  { value: "v2c", label: "v2c" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="snmp_port"
              label="Port"
              labelCol={{ span: 10 }}
              wrapperCol={{ span: 14 }}
            >
              <InputNumber min={1} max={65535} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        {/* Wansight-parity per-sensor knobs. Default values match Wansight
            ("On" + "Extended") so an existing sensor with no row in the
            modal still behaves like the recommended setup. */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="ip_validation_mode"
              label="IP Validation"
              tooltip="Decides which flows are counted based on whether src/dst is inside the IP Zone. Wansight default is 'On' (src or dst in zone). 'Strict' = exactly one side in zone (drops intra-zone). 'Exclusive' = dst in zone only (inbound). 'Off' counts everything that touches at least one zone node."
            >
              <Select
                options={[
                  { value: "on", label: "On (src OR dst in zone) — recommended" },
                  { value: "strict", label: "Strict (XOR — drops intra-zone)" },
                  { value: "exclusive", label: "Exclusive (dst in zone only)" },
                  { value: "off", label: "Off (no validation)" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="stats_engine"
              label="Stats Engine"
              tooltip="Controls how much per-flow work the sensor does. Basic = internal IPs + ports only (lowest RAM). Extended = + AS / Country tops (Wansight default; recommended). Full = + per-external-IP tops (RAM ×3-5). Full is currently equivalent to Extended in sflow — external-IP tops are a P1.2 follow-up."
            >
              <Select
                options={[
                  { value: "basic", label: "Basic (internal IPs only)" },
                  { value: "extended", label: "Extended (recommended)" },
                  { value: "full", label: "Full (+ external IPs)" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Traffic Class — per-sensor IXP/NIX override. Default "auto"
            keeps GeoIP classification. Set ixp/nix when this sensor's
            switch port carries exactly one exchange (physical split), so
            the class no longer depends on the remote IP's country and the
            geo_class alert filter / per-class graphs reflect the port. */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="traffic_class"
              label="Traffic Class"
              tooltip="How this sensor's flows are split into IXP (international) vs NIX (domestic). Auto = classify each flow by GeoIP (remote IP in Vietnam → NIX, else IXP). IXP / NIX = force EVERY flow on this sensor into that bucket and skip GeoIP — use when you've already separated IXP and NIX onto different switch ports feeding different sensors. A 'geo_class = ixp' alert rule then sums exactly the IXP-tagged sensors."
            >
              <Select
                options={[
                  { value: "auto", label: "Auto (classify by GeoIP) — default" },
                  { value: "ixp", label: "IXP — force international" },
                  { value: "nix", label: "NIX — force domestic" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Sampling Rate — only relevant for NetFlow/IPFIX. sFlow carries
            the rate in every packet so manual override would be wrong there. */}
        {protocol !== "sflow" && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sampling_rate"
                label="Sampling Rate"
                tooltip={
                  protocol === "auto"
                    ? "When protocol is Auto-detect and the source turns out to be sFlow, this field is ignored — sFlow embeds the rate in every sample. For NetFlow v9 / IPFIX exporters (e.g. Cisco IOS-XR with `sampler-map random 1 out-of N`) that never send Options Data, set N here so sfsensor can scale flows correctly. Leave at 0 if the router sends Options Data with the rate."
                    : "Manual override for the NetFlow/IPFIX sampling rate. Use when your router runs `sampler-map random 1 out-of N` (e.g. Cisco IOS-XR) but never sends the matching Options Data record — without this, sfsensor defaults to rate=1 and under-reports bandwidth N×. Leave at 0 to auto-detect from Options Data on the wire."
                }
              >
                <InputNumber
                  min={0}
                  max={1000000}
                  style={{ width: "100%" }}
                  placeholder="0 = auto-detect, e.g. 100 = 1:100"
                />
              </Form.Item>
            </Col>
            <Col span={12} />
          </Row>
        )}

        {/* Flow timeouts (Plan P1.3). Only meaningful for NetFlow/IPFIX —
            sFlow carries no flow start/end timestamps so the aggregator
            ignores both fields on sFlow records. The values should mirror
            the router's `flow active-timeout` / `flow inactive-timeout`
            so the pro-rate clamp matches the actual export cadence. */}
        {protocol !== "sflow" && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="flows_timeout_active"
                label="Flows Timeout — Active (s)"
                tooltip="Match the router's `flow active-timeout`. Long-lived flows are flushed by the exporter every N seconds. The aggregator clamps a flow's pro-rate duration at 2× this value so a clock-drifted FlowStart can't fan out across hundreds of bins. Cisco/Juniper default 60."
              >
                <InputNumber
                  min={1}
                  max={3600}
                  style={{ width: "100%" }}
                  placeholder="60"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="flows_timeout_inactive"
                label="Flows Timeout — Inactive (s)"
                tooltip="Match the router's `flow inactive-timeout`. Idle flows are flushed by the exporter after N seconds of no traffic. Currently informational; the aggregator surfaces it for diag purposes but doesn't gate behaviour on it. Cisco/Juniper default 30."
              >
                <InputNumber
                  min={1}
                  max={3600}
                  style={{ width: "100%" }}
                  placeholder="30"
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Granularity (P3.2) — aggregator bin width.
            Default 5 s suits sFlow real-time and small-to-medium NetFlow
            deployments. 20 s / 60 s trade peak-detection detail for
            InfluxDB write rate; recommended only on >10-sensor or
            >500k-IP-zone boxes. Multi-sensor charts auto-clamp the
            GROUP BY width to the largest configured granularity. */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="granularity_seconds"
              label="Granularity"
              tooltip="Aggregator bin width. 5 s = finest detail, default. 20 s / 60 s reduce InfluxDB cardinality on large sensor counts but make peak detection coarser. Operators usually leave this at 5 s unless write rate is a measured bottleneck."
            >
              <Select
                options={[
                  { value: 5, label: "5 seconds (default — finest)" },
                  { value: 20, label: "20 seconds" },
                  { value: 60, label: "60 seconds (coarsest)" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <fieldset
          style={{
            marginTop: 8,
            border: "1px solid var(--line)",
            padding: 0,
          }}
        >
          <legend
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: "0 6px",
              marginLeft: 8,
              color: "var(--strong)",
            }}
          >
            Monitored Interfaces
          </legend>
          <div
            style={{
              padding: 6,
              background: "var(--bg-2)",
              borderBottom: "1px solid var(--line)",
              display: "flex",
              gap: 6,
            }}
          >
            <Button size="small" icon={<PlusOutlined />} onClick={openAddIface}>
              Add Interface
            </Button>
          </div>
          <Table
            rowKey="key"
            size="small"
            dataSource={ifaces}
            columns={ifaceColumns}
            pagination={false}
            locale={{ emptyText: "No interfaces — Add Interface to insert one." }}
          />
        </fieldset>
      </Form>
      <InterfaceModal
        open={editingKey !== null}
        sensorId={sensorId}
        initial={
          editingIface
            ? {
                if_index: editingIface.if_index,
                name: editingIface.name,
                direction: editingIface.direction,
                color: editingIface.color,
                stats_mode:
                  (editingIface.stats_mode as "basic" | "extended") ||
                  "extended",
                speed_in: editingIface.speed_in ?? 0,
                speed_out: editingIface.speed_out ?? 0,
              }
            : undefined
        }
        onCancel={() => setEditingKey(null)}
        onSave={saveIface}
      />
    </Modal>
  );
}
